/**
 * ============================================
 * 🔹 Click Sorvetes — Utilitários Globais de Datas
 * ============================================
 * Padrão de data principal: DD/MM
 * Todas as funções aqui são puras e reutilizáveis.
 * Foco em:
 *  - Conversões seguras entre formatos
 *  - Cálculos de intervalo e diferença
 *  - Normalização de datas para API e Google Calendar
 */

//
// 🧩 CONVERSÕES BÁSICAS
//

/**
 * Converte string "DD/MM" para objeto Date (UTC).
 * @param {string} strDDMM - Ex: "12/06"
 * @param {number} ano - Ex: 2026
 * @returns {Date|null}
 */
export function parseDDMM(strDDMM, ano = new Date().getFullYear()) {
  if (!strDDMM || typeof strDDMM !== "string") return null;
  const [dd, mm] = strDDMM.split("/").map(Number);
  if (!dd || !mm) return null;
  return new Date(Date.UTC(ano, mm - 1, dd, 0, 0, 0));
}

/**
 * Formata um objeto Date para "DD/MM".
 * @param {Date} date
 * @returns {string}
 */
export function formatDDMM(date) {
  if (!(date instanceof Date) || isNaN(date)) return "";
  const d = String(date.getUTCDate()).padStart(2, "0");
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${d}/${m}`;
}

/**
 * Retorna a data no formato "YYYYMMDD"
 * @param {Date} date
 * @returns {string}
 */
export function formatYYYYMMDD(date) {
  if (!(date instanceof Date) || isNaN(date)) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/**
 * Converte "DD/MM" em "YYYYMMDDTHHMMSSZ" (padrão Google Calendar).
 * @param {string} data - Ex: "12/06"
 * @param {number} ano - Ex: 2026
 * @param {string} sufixo - Ex: "T090000Z"
 * @returns {string}
 */
export function formatarDataISO(data, ano = new Date().getFullYear(), sufixo = "T000000Z") {
  const date = parseDDMM(data, ano);
  if (!date) return "";
  return `${formatYYYYMMDD(date)}${sufixo}`;
}

//
// 🧮 CÁLCULOS E INTERVALOS
//

/**
 * Adiciona N dias a uma data.
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
export function addDays(date, days) {
  if (!(date instanceof Date)) return null;
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

/**
 * Calcula diferença em dias entre duas datas.
 * @param {Date} a
 * @param {Date} b
 * @returns {number}
 */
export function diffDays(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return 0;
  const diff = b.getTime() - a.getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

/**
 * Verifica se uma data está dentro de um intervalo.
 * @param {Date} dataAtual
 * @param {Date} inicio
 * @param {Date} fim
 * @returns {boolean}
 */
export function isBetween(dataAtual, inicio, fim) {
  if (!(dataAtual instanceof Date) || !(inicio instanceof Date) || !(fim instanceof Date)) return false;
  return dataAtual >= inicio && dataAtual <= fim;
}

//
// ✅ VALIDAÇÃO E COMPARAÇÃO
//

/**
 * Verifica se o objeto é uma data válida.
 * @param {Date} date
 * @returns {boolean}
 */
export function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Compara duas datas (retorna -1, 0 ou 1)
 * @param {Date} a
 * @param {Date} b
 * @returns {number}
 */
export function compareDates(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return 0;
  if (a.getTime() < b.getTime()) return -1;
  if (a.getTime() > b.getTime()) return 1;
  return 0;
}

/**
 * Retorna o ano atual (UTC)
 * @returns {number}
 */
export function getCurrentYear() {
  return new Date().getUTCFullYear();
}

//
// 🧰 OUTROS AUXILIARES
//

/**
 * Converte data no formato "DD/MM" para texto extenso.
 * Ex: "12/06" → "12 de junho"
 * @param {string} data
 * @returns {string}
 */
export function formatarDataExtenso(data) {
  if (!data) return "";
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
  ];
  const [d, m] = data.split("/").map(Number);
  if (!d || !m) return data;
  return `${d} de ${meses[m - 1]}`;
}
